import { ErrorView } from "common/components/error";
import { FetchErrorRetry } from "common/helpers/errors";
import { Component } from "react";
import debounce from "lodash-es/debounce";
import Radio from "backbone.radio";

import { store } from "common/store";
import { nextTick } from "common/utils";
import {
    number,
    rate,
    rateDisabled,
    formatAmountWithCurrentCurrency,
    getDefaultCurrentCurrency
} from "common/formats";

import { WebixDatatable } from "common/components/webix/react/Datatable";

import { Loader } from "common/helpers/HorizontalLoader";

import { fetchChartData, drillThrough } from "../widgetHelper";

export class DoublePivotTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            error: false
        };
        this.resizeMethod = debounce(this.reflowChart.bind(this), 300);
    }
    
    componentDidMount() {
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
        
        this.existingPromiseRequest = Promise.resolve(fetchChartData(this.props.dateRanges[0], this.props))
            .then((data) => {
                const processed = this.processData(data);
                this.setState({
                    loaded: true,
                    error: false,
                    data: processed
                });
            }, (error) => {
                this.setState({
                    loaded: true,
                    error: true
                });
            });
        
        window.addEventListener("resize", this.resizeMethod);
    }
    
    componentWillUnmount() {
        // Cancel any pending debounce func
        this.resizeMethod.cancel();
        window.removeEventListener("resize", this.resizeMethod);
        
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    }
    
    getAvailableHeight() {
        const container = this.props.widgetContainer;
        const headerH = container.querySelector(".widgetTitle").clientHeight;
        const tabsH = container.querySelector(".nav-tabs").clientHeight;
        const summaryBars = Array.from(container.querySelectorAll(".summary-bar")).find((sb) => sb.clientHeight > 0);
        const summaryH = summaryBars.clientHeight;
        
        return container.clientHeight - (headerH + tabsH + summaryH);
    }
    
    processData(data) {
        // sort current date range dataset
        const props = this.props;
        const { tablePivot, countField, otherCountFields } = props.subModel.get("doublePivot").reactProps;
        const otherCountField = otherCountFields[0];
        data.sort((a, b) => a[countField] < b[countField]);
        
        const current = data.slice(0, 3).map((r) => ({
            key: r[tablePivot],
            actor: r[tablePivot],
            ...r
        }));
        const topKeys = current.map((r) => r[tablePivot]);
        const theRestArray = data && data.length && data.length > 3 ? data.slice(3) : [];
        const restLength = theRestArray.length;
        if (restLength) {
            let theRest;
            const countArray = theRestArray.map((r) => r[countField]);
            const amountArray = theRestArray.map((r) => r[otherCountField]);
            if (restLength > 1) {
                theRest = {
                    key: "Others", actor: `${restLength} other currencies`, countField: countArray.reduce((a, b) => a + b), otherCountField: "Multiple currencies"
                };
            } else {
                theRest = {
                    key: "Others", actor: theRestArray[0][tablePivot], countField: countArray.reduce((a, b) => a + b), otherCountField: amountArray.reduce((a, b) => a + b)
                };
            }
            current.push(theRest);
        }
        
        topKeys.push("Others");
        return { keys: topKeys, current };
    }
    
    reflowChart() {
        if (this.chart) {
            // this.chart.reflow();
            this.chart.setSize(null, this.getAvailableHeight());
        }
    }
    
    drillDown(category) {
        drillThrough(this.props, category);
    }
    
    render() {
        if (this.state.error) {
            return (
                <ErrorView
                    captureInSentry={false}
                    error={new FetchErrorRetry({
                        message: "Please refresh widget as data could not load",
                        customActions: [{
                            className: "retry-widget",
                            label: "Refresh widget"
                        }],
                        hideDetails: true,
                        custom: true
                    })}
                />
            );
        }
        
        const self = this;
        if (this.state.loaded) {
            const {
                tableHeaders,
                countField,
                otherCountFields,
                countFieldGetter,
                otherCountFieldGetter
            } = this.props.subModel.get("doublePivot").reactProps;
            
            const props = {
                ui: {
                    height: 267,
                    borderless: true,
                    scroll: false,
                    rowHeight: 30,
                    headerRowHeight: 30,
                    fixedRowHeight: false,
                    on: {
                        onItemClick({ column, row }) {
                            const rowData = this.getItem(row);
                            if (rowData.key !== "Others") {
                                nextTick(() => {
                                    self.drillDown(rowData.actor);
                                });
                            }
                        }
                    },
                    columns: [
                        {
                            id: "key",
                            hidden: true
                        },
                        {
                            id: "actor",
                            header: {
                                text: tableHeaders[0],
                                css: "first-column-header"
                            },
                            fillspace: true
                        },
                        {
                            id: countField,
                            css: "not-first-column sorted-column",
                            header: {
                                text: tableHeaders[1],
                                css: "sorted-column"
                            },
                            fillspace: true,
                            template: (obj, common, originalValue, config) => {
                                const { valueType } = Radio.channel("main").request("get:getters").findById(countFieldGetter);
                                let v = obj[config.id] || 0;
                                if (valueType === "amount") {
                                    const { decimals } = getDefaultCurrentCurrency();
                                    v = formatAmountWithCurrentCurrency(v, {
                                        abbreviate: true, symbol: "", precision: decimals
                                    });
                                }
                                if (valueType === "rate") {
                                    if (store.getState().common.user_permissions.data_access_calculated_fields || self.hasOverride("data_access_calculated_fields")) {
                                        v = rate(v);
                                    } else {
                                        v = rateDisabled(v);
                                    }
                                }
                                if (valueType === "number") {
                                    v = number(v);
                                }
                                return v;
                            }
                        },
                        {
                            id: otherCountFields[0],
                            css: "not-first-column",
                            header: {
                                text: tableHeaders[2],
                                css: { "text-align": "right", "padding-right": "4px" }
                            },
                            fillspace: true,
                            template: (obj, common, originalValue, config) => {
                                const { valueType } = Radio.channel("main").request("get:getters").findById(otherCountFieldGetter);
                                let v = obj[config.id] || 0;
                                if (valueType === "amount") {
                                    const { decimals } = getDefaultCurrentCurrency();
                                    v = formatAmountWithCurrentCurrency(v, {
                                        abbreviate: true, symbol: "", precision: decimals
                                    });
                                }
                                if (valueType === "rate") {
                                    if (store.getState().common.user_permissions.data_access_calculated_fields || self.hasOverride("data_access_calculated_fields")) {
                                        v = rate(v);
                                    } else {
                                        v = rateDisabled(v);
                                    }
                                }
                                if (valueType === "number") {
                                    v = number(v);
                                }
                                return v;
                            }
                        }
                    ]
                },
                data: {
                    data: this.state.data.current
                }
            };
            return <WebixDatatable {...props}/>;
        }
        const loaderStyle = {
            paddingTop: "100px",
            width: "100%"
        };
        const style = {
            width: "100%",
            height: "100%"
        };
        
        return (<div style={style} className="chartArea"><Loader style={loaderStyle} loaded={this.state.loaded} /></div>);
    }
}
